import { isArray, isFunction, isObject } from "remeda";
import { logger } from "../../datadog/logger";
import { memoizedDeserializerWithInitialValue, withRetry } from "../../utils";
import {
  DEFAULT_VIDEO_SETTINGS,
  STORED_AUDIO_VIDEO_SETTINGS_KEY,
} from "./constants";
import { QualityMode } from "./room/participant/media/types";
import { RemoteParticipant } from "./room/participant/RemoteParticipant";
import { parseIdentity } from "./room/participant/utils";
import { RoomDelegator } from "./room/RoomDelegator";
import { AudioVideoSettings, BackgroundType, Constraint } from "./types";
import { UserType } from "../../types";

/**
 * Returns a quality mode that the UI supports i.e. only low and high quality
 */
export function consolidateQualityMode(
  qualityMode: QualityMode | null | undefined,
) {
  switch (qualityMode) {
    case QualityMode.High: // falls through
    case QualityMode.Medium:
      return QualityMode.High;
    default:
      return QualityMode.Low;
  }
}

function consolidateBackgroundType(
  backgroundType: BackgroundType | null | undefined,
) {
  switch (backgroundType) {
    case BackgroundType.BLUR: // falls through
    case BackgroundType.IMAGE:
      return backgroundType;
    default:
      return BackgroundType.NONE;
  }
}

export function getStoredAudioVideoSettings(): AudioVideoSettings {
  try {
    const rawSettings = localStorage.getItem(STORED_AUDIO_VIDEO_SETTINGS_KEY);

    if (!rawSettings) return DEFAULT_VIDEO_SETTINGS;

    const settings = JSON.parse(rawSettings) as Partial<AudioVideoSettings>;

    // trust but verify
    return {
      isAudioEnabled:
        settings.isAudioEnabled !== undefined
          ? Boolean(settings.isAudioEnabled)
          : DEFAULT_VIDEO_SETTINGS.isAudioEnabled,
      isVideoEnabled:
        settings.isVideoEnabled !== undefined
          ? Boolean(settings.isVideoEnabled)
          : DEFAULT_VIDEO_SETTINGS.isVideoEnabled,
      inputAudioDeviceId: settings.inputAudioDeviceId
        ? String(settings.inputAudioDeviceId)
        : undefined,
      inputVideoDeviceId: settings.inputVideoDeviceId
        ? String(settings.inputVideoDeviceId)
        : undefined,
      videoQualityMode: consolidateQualityMode(settings.videoQualityMode),
      backgroundType: consolidateBackgroundType(settings.backgroundType),
      videoIsMirrored: Boolean(settings.videoIsMirrored),
      selfViewIsHidden: Boolean(settings.selfViewIsHidden),
      noiseCancellationIsEnabled: Boolean(settings.noiseCancellationIsEnabled),
      backgroundIndex:
        settings.backgroundIndex !== undefined
          ? Number(settings.backgroundIndex)
          : undefined,
    };
  } catch (error) {
    logger.warn("Error parsing stored settings", {}, error as Error);
  }
  return DEFAULT_VIDEO_SETTINGS;
}

export function roomToDiagnosticInfo(room: RoomDelegator) {
  const { localParticipant, sid, name } = room;
  const { id, userType } = parseIdentity(localParticipant?.identity);
  return {
    localParticipantSid: localParticipant?.sid,
    localUserId: id,
    localUserType: userType,
    roomSid: sid,
    roomName: name,
  };
}

export function remoteParticipantToDiagnosticInfo(
  participant: RemoteParticipant,
) {
  const { identity, sid } = participant;
  const { id, userType } = parseIdentity(identity);
  return {
    participantSid: sid,
    participantUserId: id,
    participantUserType: userType,
  };
}

export function exitPictureInPicture() {
  if (document.pictureInPictureElement) {
    document.exitPictureInPicture();
  }
}

/**
 * Extracts a single value from a constraints object. **Do not use this with
 * multi-value constraint arrays, as this function will only return the first
 * value**.
 */
export function constraintsToValue<ReturnType>(
  constraint: Constraint,
  { shouldLog }: { shouldLog?: boolean } = { shouldLog: true },
) {
  let value;
  const handleArray = (list: Array<string>) => {
    if (list.length > 1 && shouldLog) {
      logger.warn("constraintsToValue called with multi-value array", {
        constraint,
      });
    }
    return list[0];
  };
  if (isArray(constraint)) {
    value = handleArray(constraint);
  } else if (isObject(constraint)) {
    if (isArray(constraint.exact)) {
      value = handleArray(constraint.exact);
    } else if (isArray(constraint.ideal)) {
      value = handleArray(constraint.ideal);
    } else {
      value = constraint.exact ?? constraint.ideal;
    }
  } else {
    value = constraint;
  }

  return value as ReturnType;
}

export function hasParticipant(
  participants: Array<RemoteParticipant>,
  participant: RemoteParticipant,
) {
  return participants.some((p) => p.identity === participant.identity);
}

export const deserializerToEnsureObjectEquality =
  memoizedDeserializerWithInitialValue(DEFAULT_VIDEO_SETTINGS, (error) => {
    logger.error("JSON parse error", undefined, error);
  });

/**
 * Wrapper to handle fire-and-forget async operations from event handlers.
 * Catches any Promise rejections to prevent unhandled promise rejections.
 */
export function handleUnhandledEventHandlerException(
  result: void | Promise<void>,
  context: string,
): void {
  if (result instanceof Promise) {
    result.catch((error) => {
      logger.error(`Encountered unhandled exception in ${context}`, { error });
    });
  }
}

/**
 * Sets the stored audio/video settings in localStorage and dispatches a storage
 * event to notify `usehooks-ts.useLocalStorage` listeners.
 */
export function setStoredAudioVideoSettings(
  updater:
    | Partial<AudioVideoSettings>
    | ((prev: AudioVideoSettings) => AudioVideoSettings),
): void {
  const updatedSettings = isFunction(updater)
    ? updater(getStoredAudioVideoSettings())
    : updater;
  localStorage.setItem(
    STORED_AUDIO_VIDEO_SETTINGS_KEY,
    JSON.stringify(updatedSettings),
  );
  // https://github.com/juliencrn/usehooks-ts/blob/20667273744a22dd2cd2c48c38cd3c10f254ae47/packages/usehooks-ts/src/useLocalStorage/useLocalStorage.ts#L141
  window.dispatchEvent(
    new StorageEvent("local-storage", { key: STORED_AUDIO_VIDEO_SETTINGS_KEY }),
  );
}

export class RoomValidationError extends Error {
  kind: "empty-room";
  constructor(kind: "empty-room") {
    super(kind);
    this.name = "RoomValidationError";
    this.kind = kind;
    this.stack = new Error().stack;
  }
}

export class GenericRoomError extends Error {
  kind: "connection-error";
  code: number | undefined;

  constructor({
    kind,
    message,
    code,
  }: {
    kind: "connection-error";
    message?: string;
    code?: number;
  }) {
    super(message || kind);
    this.name = "GenericRoomError";
    this.kind = "connection-error";
    this.stack = new Error().stack;
    this.code = code;
  }
}

/**
 * Validates the room state. Throws a {@link RoomValidationError} if the room is
 * invalid. The caller should leave the room if an error is thrown.
 */
export async function validateRoom(room: RoomDelegator) {
  const { userType } = parseIdentity(room.localParticipant?.identity);

  if (userType === UserType.CLIENT) {
    // Check again after a delay, sometimes it takes a moment for the
    // information to propagate
    await withRetry(
      async () => {
        if (room.remoteParticipants.size < 1) {
          throw new RoomValidationError("empty-room");
        }
      },
      {
        retries: 4,
        initialDelayMs: 250,
      },
    )();
  }
}
